import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, useScroll, useTransform } from "framer-motion";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import Seo from "../components/Seo";
import SearchModule from "../components/SearchModule";
import ChefCard from "../components/ChefCard";
import { MaskedLine, Reveal, Marquee, Eyebrow, GoldButton } from "../components/motion";
import { ArrowRight, BadgeCheck } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const TRUST_IMG = "https://images.pexels.com/photos/4253315/pexels-photo-4253315.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const CTA_IMG = "https://images.pexels.com/photos/14646752/pexels-photo-14646752.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1600";

export default function Home() {
  const { t, lang } = useLang();
  const { data: chefs } = useQuery({ queryKey: ["chefs-home"], queryFn: () => api.get("/chefs").then((r) => r.data) });
  const { data: experiences } = useQuery({ queryKey: ["experiences", lang], queryFn: () => api.get(`/experiences?lang=${lang}`).then((r) => r.data) });
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 220]);
  const heroScale = useTransform(scrollY, [0, 800], [1.05, 1.18]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Chef à Domicile",
    url: "https://chef-adomicile.com",
    slogan: "Exceptional Cuisine. At Your Home.",
    description: t("seo.homeDesc"),
  };

  return (
    <div>
      <Seo title={t("seo.homeTitle")} description={t("seo.homeDesc")} jsonLd={jsonLd} image={HERO_IMG} />

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden" data-testid="home-hero">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Fine dining plating" className="w-full h-full object-cover" fetchpriority="high" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0A0A0A]" />
        <div className="relative max-w-[1440px] mx-auto px-4 sm:px-8 w-full pt-32 pb-16">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="text-xs uppercase tracking-[0.35em] text-gold mb-6 flex items-center gap-3">
            <span className="inline-block w-10 h-px bg-gold" /> {t("hero.badge")}
          </motion.p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] text-ivory max-w-4xl">
            <MaskedLine delay={0.35}>{t("hero.line1")}</MaskedLine>
            <MaskedLine delay={0.55} className="text-gold italic">{t("hero.line2")}</MaskedLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-6 text-base text-neutral-300 max-w-xl leading-relaxed">
            {t("hero.sub")}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9 }} className="mt-12">
            <SearchModule />
          </motion.div>
        </div>
      </section>

      <Marquee text={t("home.marquee")} />

      {/* FEATURED CHEFS */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 py-24 md:py-32" data-testid="featured-chefs-section">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <Eyebrow>{t("home.featuredEyebrow")}</Eyebrow>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-ivory">{t("home.featuredTitle")}</h2>
            </div>
            <Link to={`/${lang}/chefs`} data-testid="home-view-all-chefs" className="gold-underline text-sm text-gold flex items-center gap-2">
              {t("home.viewAll")} <ArrowRight size={15} className="rtl:rotate-180" />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {(chefs || []).slice(0, 6).map((c, i) => <ChefCard key={c.id} chef={c} index={i} />)}
        </div>
      </section>

      {/* EXPERIENCES */}
      <section className="bg-coal border-y border-white/5 py-24 md:py-32" data-testid="home-experiences-section">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-8">
          <Reveal>
            <Eyebrow>{t("home.expEyebrow")}</Eyebrow>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-ivory mb-14">{t("home.expTitle")}</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/8 border border-white/8">
            {(experiences || []).map((x, i) => (
              <Reveal key={x.id} delay={i * 0.06}>
                <Link to={`/${lang}/experiences`} data-testid={`experience-card-${x.slug}`}
                  className="group block bg-coal hover:bg-coal2 p-8 md:p-10 h-full transition-colors duration-500">
                  <span className="font-serif text-gold/60 text-sm">0{i + 1}</span>
                  <h3 className="font-serif text-xl sm:text-2xl text-ivory mt-3 group-hover:text-gold transition-colors">{x.name}</h3>
                  <p className="text-sm text-neutral-400 mt-3 leading-relaxed">{x.description}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST / MANIFESTO */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 py-24 md:py-32 grid lg:grid-cols-2 gap-14 lg:gap-24 items-center" data-testid="trust-section">
        <Reveal>
          <div className="relative">
            <img src={TRUST_IMG} alt="Chef at work" loading="lazy" className="w-full aspect-[4/5] object-cover" />
            <div className="absolute -bottom-6 -end-4 sm:-end-6 bg-gold text-black px-6 py-4">
              <p className="font-serif text-lg leading-tight flex items-center gap-2"><BadgeCheck size={20} /> {t("common.verified")}</p>
            </div>
          </div>
        </Reveal>
        <div>
          <Reveal>
            <Eyebrow>{t("home.trustEyebrow")}</Eyebrow>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-ivory mb-6">{t("home.trustTitle")}</h2>
            <p className="text-neutral-400 leading-relaxed mb-10">{t("home.trustIntro")}</p>
          </Reveal>
          <ul className="space-y-4">
            {[1, 2, 3, 4, 5, 6, 7].map((n, i) => (
              <Reveal key={n} delay={i * 0.05}>
                <li className="flex items-center gap-4 border-b border-white/8 pb-4">
                  <span className="font-serif text-gold/70 text-sm w-7">0{n}</span>
                  <span className="text-sm text-neutral-200">{t(`trust.i${n}`)}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* NUMBERED MANIFESTO CHAPTERS */}
      <section className="bg-coal border-y border-white/5 py-24 md:py-32" data-testid="manifesto-section">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <Reveal><Eyebrow>{t("home.manifestoEyebrow")}</Eyebrow></Reveal>
          {[1, 2, 3].map((n, i) => (
            <Reveal key={n} delay={i * 0.08}>
              <div className="grid md:grid-cols-12 gap-6 py-12 md:py-16 border-b border-white/8 last:border-0">
                <span className="md:col-span-2 font-serif text-5xl md:text-6xl text-gold/25">0{n}</span>
                <h3 className="md:col-span-4 font-serif text-xl sm:text-2xl text-ivory">{t(`man.c${n}t`)}</h3>
                <p className="md:col-span-6 text-neutral-400 leading-relaxed">{t(`man.c${n}b`)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 md:py-40 overflow-hidden" data-testid="home-cta-section">
        <img src={CTA_IMG} alt="Dining ambiance" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/75" />
        <div className="relative text-center px-4">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ivory italic mb-10">{t("home.ctaTitle")}</h2>
            <Link to={`/${lang}/chefs`}>
              <GoldButton data-testid="home-cta-btn">{t("home.ctaBtn")} <ArrowRight size={16} className="rtl:rotate-180" /></GoldButton>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
